<script setup lang="ts">
import { X } from 'lucide-vue-next'
import type { SearchEntry } from '~/composables/stores/search'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { useSearchTypeMeta } from '@/composables/useSearchTypeMeta'

const props = defineProps<{
  item: SearchEntry
  selected: boolean
  highlightQuery?: string
  showClearOnHover?: boolean
}>()

defineEmits<{
  select: []
  hover: []
  remove: []
}>()

const { getMeta } = useSearchTypeMeta()
const filter = useFilter()
const t = useTranslator()

const meta = computed(() => getMeta(props.item.type))

// ── Text highlighting ────────────────────────────────────────────────────────

const highlightParts = computed(() => {
  const text = props.item.search.name
  const query = props.highlightQuery?.trim()
  if (!query || query.length <= 1) return [{ text, highlight: false }]
  const escaped = query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
  const regex = new RegExp(`(${escaped})`, 'gi')
  const parts: { text: string; highlight: boolean }[] = []
  let lastIndex = 0
  let match: RegExpExecArray | null
  while ((match = regex.exec(text)) !== null) {
    if (match.index > lastIndex) parts.push({ text: text.slice(lastIndex, match.index), highlight: false })
    parts.push({ text: match[1], highlight: true })
    lastIndex = regex.lastIndex
  }
  if (lastIndex < text.length) parts.push({ text: text.slice(lastIndex), highlight: false })
  return parts.length ? parts : [{ text, highlight: false }]
})

// ── Subtitle fields ──────────────────────────────────────────────────────────

const subtitleParts = computed(() => {
  const parts: { label: string; value: string }[] = []
  if (props.item.fields?.external_id) parts.push({ label: t('CHROME_SEARCH_ITEM_ID'), value: props.item.fields.external_id })
  if (props.item.fields?.portfolio) parts.push({ label: t('CHROME_SEARCH_ITEM_PORTFOLIO'), value: props.item.fields.portfolio })
  if (props.item.fields?.type) parts.push({ label: t('CHROME_SEARCH_ITEM_TYPE'), value: filter.capitalize(props.item.fields.type) })
  return parts
})
</script>

<template>
  <div
    role="option"
    :aria-selected="selected"
    class="w-full flex items-center gap-3 px-4 py-2.5 text-left transition-colors cursor-pointer group"
    :class="selected ? 'bg-muted/70' : 'hover:bg-muted/40'"
    @mouseenter="$emit('hover')"
    @click="$emit('select')"
  >
    <!-- Type icon -->
    <div
      class="flex items-center justify-center shrink-0"
      :style="{ width: '1.4375rem', height: '1.4375rem', background: meta.bg }"
    >
      <component
        :is="meta.icon"
        class="w-3.75 h-3.75 shrink-0"
        :style="{ color: meta.color }"
        :strokeWidth="1.7"
      />
    </div>

    <!-- Name + subtitle -->
    <div class="flex-1 min-w-0">
      <span class="text-[0.8125rem] text-foreground leading-snug truncate block">
        <template v-for="(part, pi) in highlightParts" :key="pi">
          <mark v-if="part.highlight" class="bg-transparent font-semibold text-foreground">{{ part.text }}</mark>
          <template v-else>{{ part.text }}</template>
        </template>
      </span>
      <div v-if="subtitleParts.length" class="flex items-center gap-2 mt-px">
        <span
          v-for="(part, pi) in subtitleParts"
          :key="pi"
          class="text-[0.719rem] text-muted-foreground leading-tight truncate"
        >
          <span class="text-muted-foreground/60">{{ part.label }}</span> {{ part.value }}
        </span>
      </div>
    </div>

    <!-- Badge / clear button -->
    <div v-if="showClearOnHover" class="relative shrink-0">
      <Badge
        variant="outline"
        class="text-[0.656rem] px-1.5 py-px whitespace-nowrap border-0 group-hover:invisible"
        :style="{ color: meta.color, background: meta.bg }"
      >
        {{ meta.label }}
      </Badge>
      <Button
        variant="ghost"
        size="icon"
        class="absolute inset-0 m-auto opacity-0 group-hover:opacity-100"
        @click.stop="$emit('remove')"
      >
        <X class="w-3.5 h-3.5" />
      </Button>
    </div>
    <template v-else>
      <Badge
        variant="outline"
        class="text-[0.656rem] px-1.5 py-px whitespace-nowrap shrink-0 border-0"
        :style="{ color: meta.color, background: meta.bg }"
      >
        {{ meta.label }}
      </Badge>
      <span v-if="selected" class="text-[0.625rem] text-muted-foreground/40 shrink-0 ml-1">↵</span>
    </template>
  </div>
</template>
